"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../../atoms/button/button";
import styles from "./parallax-marquee.module.css";
import type { ParallaxMarqueeStage } from "./parallax-marquee.types";
import { GLIDE_SECONDS, LONGEST_WAIT_SECONDS } from "./rotation-limits.constants";
import { withSafeRender } from "../../atoms/safe-render";

/** The marquee's client half. "use client" is earned by RUNTIME MEASUREMENT —
 *  the drawn width of the picture against its tier's published widths — and by
 *  the row being steppable. It owns which title is shown, never what it says. */

/** Names the control by what it stops, and by both rows: it stops the drifting
 *  smaller row and the slideshow alike, so a name for one is a lie about the
 *  other. */
export const PAUSE_LABEL = "Pause the moving titles";
export const PLAY_LABEL = "Play the moving titles";

/** What the block is, for a reader who meets it through its role. */
export const CAROUSEL_ROLE_DESCRIPTION = "carousel";
export const CAROUSEL_LABEL = "Featured titles";

/** The marks the row writes and the shell reads. ⚠ Neither a ref nor a handler
 *  crosses into a Server Component, so each is written on both sides of the
 *  boundary and the spec asserts they still meet. */
const MEASURE_MARK = "data-pm-measure";
const SELECT_MARK = "data-pm-selects";
const STEP_MARK = "data-pm-stepping";
export const MEASURED_PICTURE = `[${MEASURE_MARK}]`;
export const MEASURED_ROW = "[data-pm-row]";
export const SELECTS = `[${SELECT_MARK}]`;
export const STEPPING = `[${STEP_MARK}]`;

/** The control that stops the block and starts it again. */
const ROTATION_CONTROL = "[data-pm-control]";

/** One end of a thumbnail, which the shown title draws and no peek may. */
const SLOT = "[data-slot]";

/** The title being shown, and the one box in the row no step ever hides. */
const SHOWN = '[data-active="true"]';

/** Anything in the row that acts on its own, so a press on it is the browser's
 *  gesture rather than the row's. The buttons over the peeks are the row's. */
const INTERACTIVE = "a";

/** Who asked for a step: a timer step is never announced and never ends the
 *  slideshow. What a step owes the focus is read off what it hid, not off this. */
type StepBy = "hand" | "timer";

/** How far across the row a finger travels before it is a swipe rather than a
 *  press. ⚠ ASSUMED — no annotation gives a distance. */
export const SWIPE_MIN_PX = 40;

/** A place in the row brought back inside it, so a step past either end lands
 *  at the other. ⚠ REMAINDER TWICE — `%` keeps the sign in JavaScript. */
export function wrapIndex(index: number, total: number): number {
  if (!Number.isFinite(index) || !(total > 0)) {
    return 0;
  }

  return ((Math.trunc(index) % total) + total) % total;
}

/** The setting that stops the shrink being scrubbed against the scroll. */
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Slack on every width comparison: a percentage of a fractional viewport
 *  rarely resolves to a whole number. */
export const MEASURE_EPSILON_PX = 0.5;

/** How far past a boundary the picture travels before the step behind it is
 *  given up. ⚠ NOT A SECOND EPSILON: the slack above is symmetric, so without
 *  this a picture resting a fraction from one toggles on any jitter. */
export const STAGE_DEADBAND_PX = 2;

/** Below this a write moves nothing and the compositor still pays for it. */
const PROGRESS_STEP = 0.001;

/** How softly the drawn shrink follows the scroll: the time it takes to close
 *  ~63% of the distance to where the scroll has gone. */
export const SHRINK_FOLLOW_MS = 200;

/** The scroll the shrink runs over, the stylesheet's travel box less its hold:
 *  the rest of the travel keeps the resting block pinned. */
export const SHRINK_TRAVEL_PX = 400;

/** How long a step's mark survives an end event that never arrived. ⚠ A
 *  FALLBACK AND NOT THE CLOCK: the glide's own length is the stylesheet's, and
 *  the spec pins this above it so the mark cannot cut a step short. */
export const STEP_RELEASE_MS = 400;

/** `useLayoutEffect` in the browser and `useEffect` on the server, where the
 *  first warns. The measurement has to land BEFORE the first paint, or the block
 *  paints its resting picture for a frame and then snaps to the opening one. */
const useMeasureEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Whether the reader asked for less motion. ⚠ READ IN AN EFFECT, NEVER IN A
 *  RENDER: no server can know the setting, and jsdom ships no matchMedia. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

/** Whether a reader is standing anywhere inside `scope`. ⚠ THE DOCUMENT HAS TO
 *  HOLD IT TOO: `activeElement` survives the window being blurred, and a block
 *  in a background tab has nobody standing in it. */
function holdsBlock(scope: HTMLElement): boolean {
  const active = document.activeElement;

  return (
    document.hasFocus() &&
    active !== null &&
    active !== document.body &&
    scope.contains(active)
  );
}

/** Whether the focus is on something inside `scope` that a step back would
 *  hide. The title being shown is not: no step hides it. */
function holdsFocus(scope: HTMLElement): boolean {
  const active = document.activeElement;

  return holdsBlock(scope) && active !== null && !active.hasAttribute("data-active");
}

/** Whether the focus is on a peek — its own way in, or the box under one. */
function holdsPeek(scope: HTMLElement): boolean {
  const active = document.activeElement;

  return (
    active instanceof Element &&
    scope.contains(active) &&
    (active.matches(SELECTS) || active.getAttribute("data-active") === "false")
  );
}

/** Whether the focus is on the rotation control. ⚠ A PRESS FOCUSES IT ON
 *  PURPOSE, so a mouse reader is left standing there: held for, it would stop
 *  the picture ever growing back for anyone who had pressed pause. */
function holdsControl(scope: HTMLElement): boolean {
  const active = document.activeElement;

  return (
    active instanceof Element &&
    scope.contains(active) &&
    active.closest(ROTATION_CONTROL) !== null
  );
}

/** Whether the pass that has just run took `element` off the screen, by either
 *  of the two hides the row writes — `hidden`, or `data-off` for the
 *  stylesheet's `visibility`. A focus on either is dropped (WCAG 2.4.3). */
function lost(element: HTMLElement): boolean {
  return element.closest("[hidden]") !== null || element.closest("[data-off]") !== null;
}

/** A slot there was nothing to measure, which is every rect read before a
 *  layout. ⚠ NOT 0, which is a slot measured OFF the screen. */
const UNMEASURED = -1;

/** A place past either end of what the row draws. ⚠ Apart from the above: an
 *  unread rect says nothing about a slot, and this says there is none. */
const UNDRAWN = -2;

/** How much of a box has to be on the screen before a reader is left standing
 *  on it. ⚠ INVENTED: half a box is half the ring drawn round it, the one bar
 *  between WCAG 2.4.11 and 2.4.12 not picked to fit a measurement. */
const MIN_ON_SCREEN_SHARE = 0.5;

/** How much of where `box` stands is on the screen, as a share of its own
 *  width. ⚠ THE WINDOW'S WIDTH AND NOT `documentElement.clientWidth`, which is
 *  0 where nothing is laid out and would read as a real measurement. */
function slotShare(box: HTMLElement | undefined): number {
  if (!box) {
    return UNDRAWN;
  }

  const rect = box.getBoundingClientRect();

  if (!(rect.width > 0)) {
    return UNMEASURED;
  }

  return clamp01(
    (Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0)) / rect.width
  );
}

/** Gives back the tab stop the row lent, once the reader has left the box it
 *  was lent on. */
function unstand(event: Event) {
  /* ⚠ A WINDOW LOSING THE FOCUS IS NOT A READER LEAVING: giving the attribute
     back on a tab switch drops that reader onto the document (WCAG 2.4.3). The
     listener stays bound so the next real leave is still heard. */
  if (!document.hasFocus()) {
    return;
  }

  const box = event.currentTarget as HTMLElement;

  box.removeAttribute("tabindex");
  box.removeEventListener("blur", unstand);
}

/** Puts the focus on a box in the row. ⚠ A TAB STOP FOR THE ASK ALONE, so no
 *  press can leave a focus on a slide; given back on the blur, because removing
 *  `tabindex` from a focused element blurs it in some engines. */
function standOn(box: HTMLElement | null | undefined) {
  if (!box) {
    return;
  }

  box.setAttribute("tabindex", "-1");
  box.focus({ preventScroll: true });

  /* `focus()` may be refused in silence, and a tab stop left on a box holding
     no focus is what the lending exists to avoid. */
  if (document.activeElement !== box) {
    box.removeAttribute("tabindex");
    return;
  }

  /* One shared listener rather than a closure per ask: `unstand` declines to
     unbind on a window blur, so closures would stack. */
  box.addEventListener("blur", unstand);
}

/** Where a step leaves a reader whose footing it took: a peek at least
 *  MIN_ON_SCREEN_SHARE on screen, the one repeating the step first, else the
 *  shown title. ⚠ A BAR, NOT A RANKING: a centred row's two peeks tie. */
function handOn(drawn: HTMLElement[], centre: number, way: number) {
  const reachable: { select: HTMLElement; share: number }[] = [];

  for (const at of [centre + way, centre - way]) {
    const select = drawn[at]?.querySelector<HTMLElement>(SELECTS);

    /* ⚠ THE SLOT IT LANDS IN, read off the box one place back: the row is still
       painted where the step began. An unmeasured slot is still a candidate;
       an undrawn one, past either seam, never is. */
    const share = slotShare(drawn[at - way]);
    const seen = share === UNMEASURED || share >= MIN_ON_SCREEN_SHARE;

    if (select && !select.hidden && select.tabIndex >= 0 && seen) {
      reachable.push({
        select,
        share
      });
    }
  }

  /* ⚠ STABLE, AND RELIED ON: a tie keeps the peek repeating the step first. */
  reachable.sort((first, next) => next.share - first.share);

  /* ⚠ ASKED AND THEN CHECKED: a way in the same pass brought back from
     `data-off` still reads `visibility: hidden` mid-transition, and the browser
     refuses the focus in silence. */
  for (const { select } of reachable) {
    select.focus({ preventScroll: true });

    if (document.activeElement === select) {
      return;
    }
  }

  standOn(drawn[centre]);
}

/** Gives up the selection a gesture over the row was making. ⚠ ONLY WHERE IT
 *  IS THE ROW'S: a selection elsewhere on the page is not this block's. */
function dropSelection(row: HTMLElement) {
  const selection = window.getSelection();

  if (selection?.anchorNode && row.contains(selection.anchorNode)) {
    selection.removeAllRanges();
  }
}

/** Marks the row for the length of one step, which is the only time the
 *  stylesheet transitions the offset, and hands back the release. ⚠ A SCROLL
 *  TAKES IT OFF: the shrink is scrubbed, and must not lag the reader's finger. */
function glide(row: HTMLElement): () => void {
  let timer = 0;

  const release = () => {
    window.clearTimeout(timer);
    row.removeAttribute(STEP_MARK);
    row.removeEventListener("transitionend", onArrival);
    window.removeEventListener("scroll", release);
  };

  /* ⚠ THE ROW'S OWN OFFSET AND NOT A CHILD'S: every title transitions
     something of its own, and all of it bubbles through here. */
  function onArrival(event: TransitionEvent) {
    if (event.target === row && event.propertyName === "transform") {
      release();
    }
  }

  row.setAttribute(STEP_MARK, "true");
  row.addEventListener("transitionend", onArrival);
  window.addEventListener("scroll", release, { passive: true });
  timer = window.setTimeout(release, STEP_RELEASE_MS);

  return release;
}

/** Which side of the shown title a box in the row stands on. */
function placeSide(box: HTMLElement, at: number, centre: number) {
  if (at === centre) {
    box.removeAttribute("data-side");
  } else {
    box.setAttribute("data-side", at < centre ? "before" : "after");
  }
}

/** A per-tier width token, in strips, or NaN where unreadable.
 *  ⚠ NOT 0 FOR AN UNREADABLE STOP — zero is a width, and a plausible one: it
 *  puts the picture at the opening step, which hides everything the block has. */
function fraction(token: string): number {
  const parsed = Number.parseFloat(token);

  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

/** Which step of the storyboard a picture of this width is at. ⚠ EVERY
 *  BOUNDARY IS A PUBLISHED WIDTH and none is a scroll offset — and it is not the
 *  reveal the pin asks for; see design-conflicts/parallax-marquee.mdx. */
export function stageFor(
  picture: number,
  revealed: number,
  minimum: number,
  card: number
): ParallaxMarqueeStage {
  /* Resting, not opening: every step but the last hides something. */
  if (![picture, revealed, minimum, card].every(Number.isFinite)) {
    return "minimum";
  }

  if (picture <= minimum + MEASURE_EPSILON_PX) {
    return "minimum";
  }

  if (picture <= revealed + MEASURE_EPSILON_PX) {
    return "revealed";
  }

  if (picture <= card + MEASURE_EPSILON_PX) {
    return "card";
  }

  return "full";
}

/** The steps in the order a reader meets them, so a step BACK can be told. */
const STORYBOARD: ParallaxMarqueeStage[] = ["full", "card", "revealed", "minimum"];

/** The step the block settles on, given the one it is already showing. ⚠ THE
 *  DEADBAND IS ON THE WAY BACK ALONE, and SUBTRACTING IS WHAT MAKES IT SO: there
 *  is no direction test below, and adding one would say the same rule twice. */
export function settledStage(
  showing: ParallaxMarqueeStage,
  picture: number,
  revealed: number,
  minimum: number,
  card: number
): ParallaxMarqueeStage {
  const next = stageFor(picture, revealed, minimum, card);

  if (next === showing) {
    return showing;
  }

  /* The test is that the overshoot has left the step ON SCREEN, not that it has
     reached the new one — a picture jumping several steps at once lands past a
     boundary it never crossed, and asking there would hold it for ever. */
  const overshot = stageFor(picture - STAGE_DEADBAND_PX, revealed, minimum, card);

  return overshot === showing ? showing : next;
}

/** How far the neighbours have SLID in — 0 at the revealed width and 1 at rest.
 *  ⚠ THE POSITION HALF ALONE: their opacity runs an earlier leg, because both
 *  frames drawing a neighbour draw it at 30%. See --pm-neighbour-fade. */
export function peekFor(picture: number, revealed: number, minimum: number): number {
  const span = revealed - minimum;

  if (!Number.isFinite(picture) || !Number.isFinite(span) || span <= 0) {
    return 1;
  }

  return clamp01((revealed - picture) / span);
}

/** The pause mark: two bars, drawn to the control's own 24px box. */
function PauseGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 24 24"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 5H10.5C10.7761 5 11 5.22386 11 5.5V18.5C11 18.7761 10.7761 19 10.5 19H9C8.72386 19 8.5 18.7761 8.5 18.5V5.5C8.5 5.22386 8.72386 5 9 5ZM13.5 5H15C15.2761 5 15.5 5.22386 15.5 5.5V18.5C15.5 18.7761 15.2761 19 15 19H13.5C13.2239 19 13 18.7761 13 18.5V5.5C13 5.22386 13.2239 5 13.5 5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The play mark, drawn once the strip is stopped. */
function PlayGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 24 24"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.2493 11.1349C18.8322 11.5195 18.8322 12.4805 18.2493 12.8651L8.77473 19.1163C8.15597 19.5245 7.375 19.0269 7.375 18.2512V5.74878C7.375 4.97308 8.15597 4.47547 8.77473 4.88369L18.2493 11.1349Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface MarqueeShellProps {
  /** The row of titles, drawn whole by the row itself. Nothing in it is read
   *  here beyond the one picture `MEASURED_PICTURE` marks. */
  children: React.ReactNode;
  /** A step held where it is put, however wide the picture measures. Absent,
   *  the block measures the step for itself. */
  pinned?: ParallaxMarqueeStage;
  /** How long each title is held before the row brings the next one in, in
   *  seconds and already judged by the block. 0 — the default — is a row that
   *  moves only when a reader moves it. */
  rotationSeconds?: number;
  /** The smaller row of artwork, drawn by the row itself. Absent, nothing
   *  drifts — but the row of titles may still run as a slideshow, which is the
   *  other thing the control stops. */
  strip?: React.ReactNode;
  /** How many titles the row carries, copies at the ends not counted. A count
   *  rather than the titles themselves: what a step announces is read off the
   *  row's own markup, so no copy crosses the boundary. */
  total: number;
}

function MarqueeShellImpl({
  children,
  pinned,
  rotationSeconds = 0,
  strip,
  total
}: MarqueeShellProps) {
  /* Whether everything the block moves on its own is standing still. ⚠ ONE
     WAY OUT: a focus arriving stops it too, and only the control starts it. */
  const [stopped, setStopped] = useState(false);
  /* Which title is shown, written onto the row by attribute: every title's
     markup is already on the page. */
  const [active, setActive] = useState(0);
  const [announcement, setAnnouncement] = useState("");
  /* ⚠ THE OPENING STEP, NOT THE RESTING ONE: it is what a scroll-top visitor
     resolves to, and the two disagreeing shifts the hero's largest paint. */
  const [measured, setMeasured] = useState<ParallaxMarqueeStage>("full");
  /* Not the setting — the effects read that where they use it. This is what
     re-runs them when it changes, and `false` is the answer the server gives. */
  const [reduced, setReduced] = useState(false);
  /* Whether script has run at all; see the effect that writes it. */
  const [live, setLive] = useState(false);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  /* The step on screen, read from inside a callback without waiting for a
     render. */
  const showing = useRef<ParallaxMarqueeStage>("full");

  /* Read by listeners bound once, which a render does not rebind. */
  const activeIndex = useRef(0);
  const canStep = useRef(false);

  /* The gesture in progress. ⚠ `swiped` OUTLIVES IT by one event — the click
     below — and `stepped` is apart from it: a swipe the row could not step
     still has a click to suppress, and no selection to take. */
  const swipePointer = useRef<number | null>(null);
  const swipeFrom = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  const stepped = useRef(false);

  /* Which way the reader asked the row to go, cleared by the step that
     answers it. */
  const stepWay = useRef(0);

  /* The release of the step in flight. Held across renders: the effect that
     writes the mark re-runs for reasons that are not steps. */
  const endStep = useRef<() => void>(() => {});

  /* The stepping effect's `show`, published for the slideshow. ⚠ A REF: that
     effect binds listeners by hand, and rebuilding it for a new wait would
     rebind them under a gesture in flight. */
  const showRef = useRef<(next: number, by: StepBy, way: number) => void>(() => {});

  /* The slideshow's own end, called by a step by hand. A no-op while nothing
     rotates, so a swipe on a row that only drifts leaves the drift alone. */
  const stopRotation = useRef<() => void>(() => {});

  /* The title the control's focus was handed to by a scroll back. ⚠ THE
     BLOCK MOVED THAT FOCUS, NOT THE READER, so it stops nothing the control
     itself would not have stopped. */
  const handedFromControl = useRef<HTMLElement | null>(null);

  /* The measurement's observer and the picture it watches, so a step can
     re-point the observer rather than rebuild it. */
  const watching = useRef<{ observer: ResizeObserver; picture: HTMLElement } | null>(null);

  const hasStrip = strip !== null && strip !== undefined;
  const showingStage = pinned ?? measured;

  /* ⚠ JUDGED AGAIN HERE: the shell is imported by path, and nothing between it
     and a call site checks a number. */
  const rotates =
    total > 1 && rotationSeconds >= GLIDE_SECONDS && rotationSeconds <= LONGEST_WAIT_SECONDS;

  /* ⚠ GATED ON SCRIPT HAVING RUN: before it the press never attaches, so the
     name would promise a state it cannot change (WCAG 4.1.2). A slideshow is
     something to stop with no smaller row too (WCAG 2.2.2). */
  const pausable = (hasStrip || rotates) && live && !reduced;

  /* ⚠ THE ROW IS STEPPABLE AT THE RESTING STEP AND NOWHERE ELSE, and the row
     draws the same test on the server so the first paint agrees with it. A
     peek before then is still easing in with half of it off the strip. */
  const operable = total > 1 && showingStage === "minimum";

  /* Whether the row brings titles in of its own accord. The pointer's hold is
     not in here: it comes and goes with a passing hand, and lives in the
     slideshow's own effect. */
  const autoRotating =
    rotates && live && !reduced && !stopped && showingStage === "minimum";

  /* The setting can be turned on under a live block, and what it leaves behind
     — a picture frozen part-way through a shrink nothing will finish — is the
     one outcome it must not produce. */
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduced(query.matches);

    onChange();

    /* ⚠ A MediaQueryList IS NOT AN EventTarget ON SAFARI 13 AND OLDER EMBEDDED
       WEBVIEWS: `addEventListener` is absent there and calling it throws from a
       committed effect. Remove the pair when browserslist rules those out. */
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);

      return () => query.removeEventListener("change", onChange);
    }

    query.addListener(onChange);

    return () => query.removeListener(onChange);
  }, []);

  /* WHICH TITLE IS SHOWN, written onto markup the server drew. ⚠ DECLARED
     FIRST OF THE LAYOUT EFFECTS: the measurement below reads the picture this
     one marks, and layout effects run in the order they are written. */
  useMeasureEffect(() => {
    const root = rootRef.current;
    const row = root?.querySelector<HTMLElement>(MEASURED_ROW);

    if (!row) {
      return;
    }

    /* 0 for a render that is not a step. */
    const way = stepWay.current;

    /* Read BEFORE the pass below hides anything: the browser blurs an element
       as it goes, and the document then no longer names it. */
    const stoodOn = document.activeElement;

    const drawn = Array.from(row.children) as HTMLElement[];
    /* One along wherever a copy of the last title stands in front. */
    const along = drawn.length > 1 ? 1 : 0;
    const centre = active + along;
    const wasAt = activeIndex.current + along;
    const from = centre - way;

    stepWay.current = 0;
    activeIndex.current = active;
    canStep.current = operable;

    row.setAttribute("data-operable", String(operable));

    /* ⚠ THE ROW IS STOOD ON THE COPY OF THE TITLE IT IS LEAVING FIRST, or a
       step across the seam glides the row its whole length back. The read is
       what keeps the two writes apart: in one task they resolve as one. */
    if (way !== 0) {
      endStep.current();

      if (from !== wasAt) {
        /* ⚠ SHOWN ON THE READ'S SIDE OF THE STEP: the glide interpolates
           `visibility` from the style that read leaves, and at progress 0 that
           is still `hidden` — so the row starts on nothing. */
        drawn
          .slice(Math.max(0, from - 1), from + 2)
          .forEach(box => box.removeAttribute("data-off"));

        /* ⚠ AND EVERY BOX ON ITS SIDE OF THE COPY: the side places a box, and
           the glide starts from wherever this read leaves it. */
        drawn.forEach((box, at) => placeSide(box, at, from));

        row.style.setProperty("--pm-index", String(from));
        row.getBoundingClientRect();
      }

      endStep.current = glide(row);
    }

    row.style.setProperty("--pm-index", String(centre));

    drawn.forEach((box, at) => {
      const shown = at === centre;
      const off = Math.abs(at - centre) > 1;

      box.setAttribute("data-active", String(shown));
      placeSide(box, at, centre);

      /* Past the peek either side there is nothing on screen, and a control
         focusable outside the strip's clip would take a reader off it. */
      if (off) {
        box.setAttribute("data-off", "true");
      } else {
        box.removeAttribute("data-off");
      }

      box.querySelectorAll<HTMLElement>(SLOT).forEach(slot => {
        slot.hidden = !shown;
      });

      const select = box.querySelector<HTMLButtonElement>(SELECTS);

      if (select) {
        select.hidden = shown || off || !operable;
      }

      /* The mark follows the title shown, so it means what it says. */
      const picture = box.querySelector("picture");

      if (shown) {
        picture?.setAttribute(MEASURE_MARK, "true");
      } else {
        picture?.removeAttribute(MEASURE_MARK);
      }
    });

    /* ⚠ AFTER THE PASS, whatever asked for the step: a press, a key and an
       assistive technology's activation all leave the focus on a way in this
       pass may just have hidden. Never scrolled to — the scroll drives the shrink. */
    if (stoodOn instanceof HTMLElement && row.contains(stoodOn) && lost(stoodOn)) {
      handOn(drawn, centre, way);
    }
  }, [active, children, operable]);

  /* ⚠ The timer and the listener a step holds are the window's, so the row
     going takes neither. */
  useEffect(() => () => endStep.current(), []);

  /* Script ran and hydration completed; the hides key off this so a page whose
     bundle never arrives keeps the resting picture. ⚠ ONE EFFECT FOR THE MARK
     AND THE STATE, so stylesheet and control cannot disagree on it. */
  useMeasureEffect(() => {
    rootRef.current?.setAttribute("data-pm-live", "true");
    setLive(true);
  }, []);

  /* THE SHRINK. Declared before the measurement below it so the first paint
     measures a picture the progress has already been applied to; layout effects
     run in the order they are written. */
  useMeasureEffect(() => {
    const root = rootRef.current;
    const scroller = scrollerRef.current;

    if (!root) {
      return undefined;
    }

    /* Pinned, stopped, or unmeasurable: the stylesheet has a value for all
       three and an inline one would beat it, so it and the travel both go.
       ⚠ NO OBSERVER IS A SILENT SHRINK — `data-stage` would stay at rest. */
    if (
      pinned !== undefined ||
      prefersReducedMotion() ||
      !scroller ||
      typeof ResizeObserver !== "function"
    ) {
      root.style.removeProperty("--pm-progress");
      root.style.removeProperty("--pm-page-below");
      root.style.removeProperty("--pm-start-top");
      root.style.removeProperty("--pm-lift");
      root.style.removeProperty("--pm-open-full");
      scroller?.removeAttribute("data-travel");
      return undefined;
    }

    /* ⚠ THE TRAVEL IS ASKED FOR RATHER THAN DECLARED, so a page whose script
       never runs has no viewport of dead scroll below a picture that will never
       move. The height itself stays in the stylesheet. */
    scroller.setAttribute("data-travel", "true");

    let frame = 0;
    let written = -1;
    let writtenBelow = -1;
    /* Where the drawn shrink is, and the time of the frame that drew it. */
    let drawn = -1;
    let drawnAt = 0;
    /* Where the block starts on the page when it opens in the first screen,
       under a nav, and how far it still sits below the top. Re-measured on a
       resize rather than per scroll, since only the scroll moves it. */
    let startTop = 0;
    let stale = true;
    let writtenLift = -1;

    /* ⚠ THE OPENING HAS TO FILL THE SCREEN BELOW THE BLOCK, and on a screen
       taller than the picture's ratio allows it cannot — so the opening width is
       raised until it does. A length over a length, which CSS cannot divide. */
    const measure = (box: DOMRect) => {
      stale = false;

      const top = box.top + window.scrollY;

      startTop = top > 0 && top < window.innerHeight ? Math.round(top) : 0;
      root.style.setProperty("--pm-start-top", `${startTop}px`);

      const image = root.querySelector<HTMLElement>(`${MEASURED_PICTURE} img`);
      const full = fraction(window.getComputedStyle(root).getPropertyValue("--pm-slide-full"));
      const ratioOpen = image ? image.offsetHeight / full : Number.NaN;
      const cover = (window.innerHeight - startTop) / ratioOpen;

      if (Number.isFinite(cover) && cover > full) {
        root.style.setProperty("--pm-open-full", cover.toFixed(5));
      } else {
        root.style.removeProperty("--pm-open-full");
      }
    };

    const read = (now?: number) => {
      frame = 0;

      /* ⚠ THE ONLY LAYOUT READS ON THE SCROLL PATH, with the write after them:
         the other order forces a synchronous layout on every notch of the
         wheel. */
      const box = scroller.getBoundingClientRect();
      /* ⚠ MEASURED, NOT window.innerHeight. `vh` is the LARGE viewport where
         `innerHeight` is whatever the URL bar has left, so dividing one by the
         other reaches 1 and unpins the block every time the bar slides. */
      const travel = box.height - root.getBoundingClientRect().height;
      /* The stylesheet's floor under the block. ⚠ THE SAME HOWEVER TALL THE
         BLOCK IS, so writing it cannot feed back into the scroll; floored, so
         any rounding leaves the travel room rather than taking it. */
      const below = Math.floor(
        document.documentElement.scrollHeight - (window.scrollY + box.bottom)
      );

      /* The ease restarts from here, or the gap since its last frame reads as
         one long frame and the next one snaps. */
      if (!(travel > 0)) {
        drawnAt = 0;

        return;
      }

      if (below !== writtenBelow) {
        writtenBelow = below;
        root.style.setProperty("--pm-page-below", `${Math.max(0, below)}px`);
      }

      if (stale) {
        measure(box);
      }

      /* A block opening under a nav starts shrinking on the first pixel of
         scroll, and is centred in what is left of the screen below it. */
      const lift = Math.round(Math.min(Math.max(box.top, 0), startTop));

      if (lift !== writtenLift) {
        writtenLift = lift;
        root.style.setProperty("--pm-lift", `${lift}px`);
      }

      const target = clamp01((startTop - box.top) / (startTop + Math.min(travel, SHRINK_TRAVEL_PX)));
      const elapsed = now === undefined || !drawnAt ? 16 : now - drawnAt;

      /* Eased toward the scroll rather than snapped to it, and landing exactly.
         ⚠ THE FIRST READ SNAPS: a page restored mid-scroll must not replay it. */
      drawn =
        drawn < 0 || Math.abs(target - drawn) < PROGRESS_STEP ?
          target :
          drawn + (target - drawn) * (1 - Math.exp(-elapsed / SHRINK_FOLLOW_MS));
      drawnAt = drawn === target ? 0 : (now ?? 0);

      if (drawn !== target) {
        frame = requestAnimationFrame(read);
      }

      if (drawn === written || (drawn !== target && Math.abs(drawn - written) < PROGRESS_STEP)) {
        return;
      }

      written = drawn;
      root.style.setProperty("--pm-progress", drawn.toFixed(4));
    };

    /* Coalesced: a wheel or a touch drag fires far more often than the screen
       is painted, and every one of those would otherwise be a read. */
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(read);
      }
    };

    /* A resize or a page that grows can move where the block starts. */
    const remeasure = () => {
      stale = true;
      schedule();
    };

    /* A section that streams in or mounts late below the block grows the page
       with no scroll to report it, and the floor would keep the old answer. */
    const page = new ResizeObserver(remeasure);

    read();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", remeasure, { passive: true });
    page.observe(document.documentElement);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", remeasure);
      page.disconnect();

      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [pinned, reduced]);

  /* THE MEASUREMENT. What the block draws is the picture's own drawn width
     against the widths its tier publishes, so it is observed rather than derived
     back out of the ramp that set it. */
  useMeasureEffect(() => {
    const root = rootRef.current;
    const picture = root?.querySelector<HTMLElement>(MEASURED_PICTURE);

    if (!root || !picture) {
      return undefined;
    }

    if (prefersReducedMotion()) {
      root.style.removeProperty("--pm-peek");
      showing.current = "minimum";
      setMeasured("minimum");
      return undefined;
    }

    /* jsdom ships no ResizeObserver, and unguarded this throws in a consumer's
       test rather than in ours. Without one nothing can be measured, so the
       block rests — the one step that hides nothing. */
    if (typeof ResizeObserver !== "function") {
      showing.current = "minimum";
      setMeasured("minimum");
      return undefined;
    }

    /* ⚠ NOT `strip` AND `art`: the shell holds a `strip` of its own, and
       `no-shadow` is off in this repo, so only the name keeps the two apart. */
    let stripWidth = 0;
    let artWidth = 0;
    let minimum = 0;
    let revealed = 0;
    let card = 0;

    /* Read off the stylesheet rather than restated here: each stop IS a published
       width. ⚠ THE STOPS MOVE AT THE VIEWPORT'S WIDTH AND THIS RUNS ON THE
       STRIP'S — re-read them off a media query if a consumer is not full-bleed. */
    const readStops = () => {
      const computed = window.getComputedStyle(root);

      minimum = fraction(computed.getPropertyValue("--pm-slide-min"));
      revealed = fraction(computed.getPropertyValue("--pm-slide-revealed"));
      card = fraction(computed.getPropertyValue("--pm-slide-card"));
    };

    const resolve = () => {
      /* ⚠ FINITE AND POSITIVE, not merely positive: a rect read before layout
         has settled, or a stop the stylesheet did not resolve, arrives as NaN,
         and a bare `<= 0` lets it straight through to the ramp. */
      if (!(stripWidth > 0) || !(artWidth > 0)) {
        return;
      }

      const rest = stripWidth * minimum;
      const reveal = stripWidth * revealed;

      root.style.setProperty("--pm-peek", peekFor(artWidth, reveal, rest).toFixed(4));

      const next = settledStage(
        showing.current,
        artWidth,
        reveal,
        rest,
        stripWidth * card
      );

      const back = STORYBOARD.indexOf(next) < STORYBOARD.indexOf(showing.current);
      const handsOff = holdsPeek(root) || holdsControl(root);

      /* ⚠ A STEP BACK HIDES WHAT THE READER IS STANDING ON and drops the focus
         to the document (WCAG 2.4.3), so it is held while focus is inside —
         bar a peek or the control, which are handed to the title instead. */
      if (back && holdsFocus(root) && !handsOff) {
        return;
      }

      /* ⚠ BEFORE THE STEP: the render below is what hides them, and the focus
         drops as it lands. Off the resting step the title is the one place left. */
      if (back && handsOff) {
        const shown = root.querySelector<HTMLElement>(SHOWN);

        handedFromControl.current = holdsControl(root) ? shown : null;
        standOn(shown);
      }

      showing.current = next;
      setMeasured(next);
    };

    /* Fires BEFORE the next element takes focus, so where it is going is read
       off the event rather than off the document. */
    const onFocusOut = (event: FocusEvent) => {
      const landing = event.relatedTarget;

      if (landing instanceof Node && root.contains(landing)) {
        return;
      }

      resolve();
    };

    const observer = new ResizeObserver(entries => {
      entries.forEach(entry => {
        if (entry.target === root) {
          stripWidth = entry.contentRect.width;
          readStops();
          return;
        }

        artWidth = entry.contentRect.width;
      });

      resolve();
    });

    /* Measured here rather than waiting for the observer's own first delivery,
       which is not guaranteed to flush before the frame is painted. ⚠ clientWidth
       because the observer reports a CONTENT box and a rect is the border box. */
    stripWidth = root.clientWidth;
    artWidth = picture.clientWidth;
    readStops();
    resolve();

    observer.observe(root);
    observer.observe(picture);
    watching.current = {
      observer,
      picture
    };
    root.addEventListener("focusout", onFocusOut);

    return () => {
      observer.disconnect();
      watching.current = null;
      root.removeEventListener("focusout", onFocusOut);
    };

    /* ⚠ `children` IS A REAL DEPENDENCY AND NOTHING HERE READS IT: it replaces
       the picture marked for measuring, so the observer has to be rebuilt.
       exhaustive-deps knows no `useMeasureEffect`. */
  }, [children, reduced]);

  /* A step moves the mark to another title's picture. ⚠ RE-POINTED, NOT
     REBUILT: every picture in the row is drawn to the same width, so a timer
     step costs no observer and no layout read. */
  useMeasureEffect(() => {
    const watched = watching.current;
    const picture = rootRef.current?.querySelector<HTMLElement>(MEASURED_PICTURE);

    if (!watched || !picture || picture === watched.picture) {
      return;
    }

    watched.observer.unobserve(watched.picture);
    watched.observer.observe(picture);
    watched.picture = picture;
  }, [active]);

  /* THE CONTROL'S FOCUS GIVEN BACK at rest: left on the title, a timer step
     hides it and the hand-on stops the slideshow. ⚠ ASKED AGAIN AS ITS FADE
     ENDS: this commit still computes `visibility: hidden`, which refuses focus. */
  useMeasureEffect(() => {
    const holder = rootRef.current?.querySelector<HTMLElement>(ROTATION_CONTROL);
    const control = holder?.querySelector<HTMLElement>("button");

    if (showingStage !== "minimum" || !handedFromControl.current) {
      return undefined;
    }

    /* At rest with no control drawn (reduced motion) there is nothing to give
       the focus back to, so the hand-off ends here as on every other exit. */
    if (!holder || !control) {
      handedFromControl.current = null;
      return undefined;
    }

    const giveBack = () => {
      if (document.activeElement !== handedFromControl.current) {
        handedFromControl.current = null;
        return;
      }

      control.focus({ preventScroll: true });

      if (document.activeElement === control) {
        handedFromControl.current = null;
      }
    };

    giveBack();

    if (!handedFromControl.current) {
      return undefined;
    }

    /* The last ask either way: a fade that ended or was cancelled at rest
       leaves nothing further to wait for. */
    const onSettled = (event: TransitionEvent) => {
      if (event.target !== holder || event.propertyName !== "visibility") {
        return;
      }

      giveBack();
      handedFromControl.current = null;
    };

    holder.addEventListener("transitionend", onSettled);
    holder.addEventListener("transitioncancel", onSettled);

    /* ⚠ A STEP BACK BEFORE THE FADE ENDS KEEPS THE HAND-OFF while the focus
       still stands on the title, so the next rest asks again; anything else
       has ended it. */
    return () => {
      holder.removeEventListener("transitionend", onSettled);
      holder.removeEventListener("transitioncancel", onSettled);

      if (document.activeElement !== handedFromControl.current) {
        handedFromControl.current = null;
      }
    };
  }, [showingStage]);

  /* STEPPING THE ROW. ⚠ BOUND BY HAND AND NOT BY REACT, because a handler
     cannot cross into the server's markup. */
  useEffect(() => {
    const root = rootRef.current;
    const row = root?.querySelector<HTMLElement>(MEASURED_ROW);

    if (!row) {
      return undefined;
    }

    /** Brings a title into the middle, and says so where a reader asked. */
    const show = (next: number, by: StepBy, way: number) => {
      const at = wrapIndex(next, total);

      /* A step to the title already shown would move the NEXT render's focus. */
      if (at === activeIndex.current) {
        return;
      }

      const title = row.children[at + (row.children.length > 1 ? 1 : 0)];
      const place = `${at + 1} of ${total}`;

      stepWay.current = way;
      setActive(at);

      /* ⚠ A TIMER STEP SAYS NOTHING: a title every few seconds would talk over
         everything else on the page. */
      if (by === "timer") {
        return;
      }

      /* A step by hand ends the slideshow. A press and a key have already
         stopped it by focusing something; a finger focuses nothing. */
      stopRotation.current();

      /* ⚠ A NODE OF ITS OWN rather than the row: a step changes two slides, and
         a live row reads them in document order. The title too, because the
         focus is left on a way in named for the NEXT title. */
      setAnnouncement(`${place}, ${title?.getAttribute("data-pm-title") ?? ""}`);
    };

    showRef.current = show;

    const release = () => {
      swipePointer.current = null;
      swipeFrom.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
    };

    /* ⚠ THE ROW STEPS AS THE THRESHOLD IS CROSSED rather than on the release,
       which by then is a gesture of nothing happening. ⚠ AND A DRAG DOWN THE
       PAGE IS NOT A SWIPE: it gives up its origin and the browser pans. */
    function onMove(event: PointerEvent) {
      const from = swipeFrom.current;

      if (event.pointerId !== swipePointer.current || !from) {
        return;
      }

      const across = event.clientX - from.x;
      const down = event.clientY - from.y;

      if (Math.abs(down) > Math.abs(across) && Math.abs(down) >= SWIPE_MIN_PX) {
        swipeFrom.current = null;
        return;
      }

      if (Math.abs(across) >= SWIPE_MIN_PX && Math.abs(across) > Math.abs(down)) {
        /* Given up before the step, so one swipe cannot step the row twice. */
        swipeFrom.current = null;
        swiped.current = true;

        /* ⚠ ASKED AGAIN HERE: a wheel under a held mouse can grow the picture
           mid-gesture, and the row must not step where no key could. */
        if (canStep.current) {
          const way = across < 0 ? 1 : -1;

          stepped.current = true;
          show(activeIndex.current + way, "hand", way);
        }
      }
    }

    function onEnd(event: PointerEvent) {
      if (event.pointerId !== swipePointer.current) {
        return;
      }

      /* ⚠ A SWIPE THAT STEPPED GIVES UP ITS SELECTION: the browser goes on
         extending it while the row steps out from under the finger. One that
         stepped nothing keeps it — nothing moved under it. */
      if (stepped.current) {
        dropSelection(row);
      }

      release();
    }

    /* ⚠ ONE POINTER AT A TIME AND THE FIRST KEEPS IT: a pinch is two fingers
       on the same row, and the second must not become the origin the first is
       measured from. A press on a link is the browser's, not the row's. */
    const onDown = (event: PointerEvent) => {
      /* Cleared by any press, before the guards, so neither outlives the
         gesture that set it. */
      swiped.current = false;
      stepped.current = false;

      if (
        swipePointer.current !== null ||
        !event.isPrimary ||
        event.button !== 0 ||
        !canStep.current ||
        (event.target as Element).closest(INTERACTIVE) !== null
      ) {
        return;
      }

      swipePointer.current = event.pointerId;
      swipeFrom.current = {
        x: event.clientX,
        y: event.clientY
      };

      /* ⚠ ON THE WINDOW AND NOT CAPTURED. `setPointerCapture` also catches a
         release let go off the row, and it retargets the compatibility click
         to the capturing element — killing the tap and the link under it. */
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onEnd);
      window.addEventListener("pointercancel", onEnd);
    };

    const onClick = (event: MouseEvent) => {
      /* ⚠ A SWIPE ENDS IN A CLICK wherever the finger came to rest, and it
         must neither step the row again nor follow a link it was dragged
         across. A keyboard's carries no detail and follows no gesture. */
      if (swiped.current && event.detail > 0) {
        swiped.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const pressed = (event.target as Element).closest(SELECTS);
      /* ⚠ READ, NOT COERCED: `Number(null)` is 0, a place the row has, so a
         missing mark would bring the FIRST title in rather than nothing. */
      const asked = pressed?.getAttribute(SELECT_MARK);

      if (asked === null || asked === undefined || !canStep.current) {
        return;
      }

      /* Which way, off the side pressed: the two places alone cannot tell a
         wrap from a rewind. */
      const side = pressed?.closest("[data-side]")?.getAttribute("data-side");

      show(Number(asked), "hand", side === "before" ? -1 : 1);
    };

    row.addEventListener("click", onClick);
    row.addEventListener("pointerdown", onDown);

    return () => {
      row.removeEventListener("click", onClick);
      row.removeEventListener("pointerdown", onDown);
      release();
    };
  }, [children, total]);

  /* A FOCUS ARRIVING IN THE CAROUSEL STOPS IT, and only the control starts it
     again — the APG carousel rule. Armed on exactly what the slideshow is, so
     it cannot turn the control to Play at a step that hides it. */
  useEffect(() => {
    const root = rootRef.current;

    if (!root || !autoRotating) {
      return undefined;
    }

    /* ⚠ READ BEFORE THE LISTENER GOES ON: a focus arriving while nothing was
       bound (before hydration, or as reduced motion turned off) raises no event.
       Exempt: the control, focused before its press, and the title it handed to. */
    function stops(target: Element): boolean {
      return target !== handedFromControl.current && target.closest(ROTATION_CONTROL) === null;
    }

    const standing = document.activeElement;

    if (holdsBlock(root) && standing !== null && stops(standing)) {
      setStopped(true);
      return undefined;
    }

    const onFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Element && stops(event.target)) {
        setStopped(true);
      }
    };

    root.addEventListener("focusin", onFocusIn);

    return () => root.removeEventListener("focusin", onFocusIn);
  }, [autoRotating]);

  /* THE SLIDESHOW, at the resting step alone: before it no peek is drawn and
     the control is hidden, so the row would move with no way to stop it. */
  useEffect(() => {
    const root = rootRef.current;

    if (!root || !autoRotating) {
      return undefined;
    }

    const wait = rotationSeconds * 1000;

    /** Asked for again at every event: a `children` change replaces the row. */
    const rowNow = (): HTMLElement | null => root.querySelector(MEASURED_ROW);

    /** Whether the pointer's node is inside the ROW. ⚠ NOT `.root`, which also
     *  holds the smaller row and the control under the card; and the hit target,
     *  not the rect, because the row's box runs off-screen past `.root`'s clip. */
    const onRow = (node: EventTarget | null): boolean => {
      const row = rowNow();

      return row !== null && node instanceof Node && row.contains(node);
    };

    /* A pointer on the row holds the wait until it leaves — a deliberate
       departure from the APG one-way hover stop (design-conflicts). ⚠ SEEDED
       OFF `:hover`: a pointer already inside fires no event. */
    let over = rowNow()?.matches(":hover") === true;
    /* On screen until the observer says otherwise, so an engine without one
       runs the slideshow wherever the block is. */
    let onScreen = true;
    let timer = 0;

    const stop = () => {
      window.clearInterval(timer);
      timer = 0;
    };

    /* ⚠ A WHOLE WAIT FROM HERE AND NEVER THE REMAINDER: a title arriving
       the instant a pointer leaves the row, or the block comes back into view,
       reads as the block reacting to the reader rather than carrying on. */
    const settle = () => {
      stop();

      if (over || !onScreen || document.hidden) {
        return;
      }

      timer = window.setInterval(() => {
        showRef.current(activeIndex.current + 1, "timer", 1);
      }, wait);
    };

    /* ⚠ ONLY WHERE THE ANSWER CHANGES: `pointerover` fires for every box the
       pointer crosses, and settling on each would restart the wait for ever
       under a moving hand. */
    const holdFor = (node: EventTarget | null) => {
      const next = onRow(node);

      if (next === over) {
        return;
      }

      over = next;
      settle();
    };

    const onOver = (event: PointerEvent) => holdFor(event.target);

    /* `relatedTarget` is where the pointer is going; `null` is off the page. */
    const onOut = (event: PointerEvent) => holdFor(event.relatedTarget);

    /* ⚠ NO STEP NOBODY CAN SEE: a block scrolled past, or in a background tab,
       would otherwise wake the page every wait for the life of the visit. The
       observer's first report is where the block already is. */
    let view: IntersectionObserver | null = null;

    if (typeof IntersectionObserver === "function") {
      view = new IntersectionObserver(entries => {
        const next = entries[entries.length - 1].isIntersecting;

        if (next !== onScreen) {
          onScreen = next;
          settle();
        }
      });
    }

    settle();
    stopRotation.current = () => setStopped(true);

    view?.observe(root);
    document.addEventListener("visibilitychange", settle);
    root.addEventListener("pointerover", onOver);
    root.addEventListener("pointerout", onOut);

    return () => {
      stop();
      /* Put back, so a step by hand on a still row leaves the drift alone. */
      stopRotation.current = () => {};
      view?.disconnect();
      document.removeEventListener("visibilitychange", settle);
      root.removeEventListener("pointerover", onOver);
      root.removeEventListener("pointerout", onOut);
    };
  }, [autoRotating, rotationSeconds]);

  return (
    <div className={styles.scroller} ref={scrollerRef}>
      {/* The carousel is this element, holding the slides and the control that
          works them. One title has nowhere to step, so it is not named one. */}
      <div
        aria-label={total > 1 ? CAROUSEL_LABEL : undefined}
        aria-roledescription={total > 1 ? CAROUSEL_ROLE_DESCRIPTION : undefined}
        className={styles.root}
        /* ⚠ A FOCUS ARRIVING STOPS THE DRIFT TOO: one control stands for
           everything the block moves on its own. */
        data-paused={stopped}
        data-stage={showingStage}
        ref={rootRef}
        role={total > 1 ? "group" : undefined}
      >
        {/* ⚠ PRESENT AND EMPTY FROM THE FIRST PAINT, and polite at every moment:
            a timer step never writes into it, so a manner that flipped would
            only ever flip in the same commit as the words a reader is owed. */}
        <div aria-atomic="true" aria-live="polite" className="dds-sr-only">
          {announcement}
        </div>

        {pausable && (
          /* One control, which is all any frame draws: its two published glass
             instances are its play and pause states. ⚠ AHEAD OF THE ROWS IN DOM
             ORDER, so a Tab reaches it before any slide (APG carousel); the
             stylesheet still draws it last. */
          <div className={styles.controls} data-pm-control="true">
            {/* The name carries the state, so no `aria-pressed` beside it. */}
            <Button
              ariaLabel={stopped ? PLAY_LABEL : PAUSE_LABEL}
              className={styles.pauseControl}
              /* ⚠ THE CONTROL TAKES ITS OWN FOCUS: Safari and Firefox move none
                 on a press, and a focus left in the row would stop the slideshow
                 again the instant Play started it (WCAG 4.1.2). */
              onClick={event => {
                event.currentTarget.focus();
                setStopped(current => !current);
              }}
              size="xSmall"
              startIcon={stopped ? <PlayGlyph /> : <PauseGlyph />}
              variant="glass"
            />
          </div>
        )}

        {children}
        {strip}
      </div>
    </div>
  );
}
export const MarqueeShell = withSafeRender(MarqueeShellImpl, "MarqueeShell");
